import { useEffect } from "react";
import { clearReturnTo } from "@itaas/core/modules/suite-navigation";
import {
  buildSuiteLoginUrl,
  clearLogoutPending,
} from "@itaas/core/auth/suite-redirect";
import { purgeAuthState } from "@itaas/core/auth/purge-auth-state";
import { syncModuleLogoutIframes } from "@itaas/core/auth/sync-module-logouts";
import { suiteSessionApi } from "@/auth/suite-auth.service";
import { listModuleBaseUrls } from "@/config";

const LOGOUT_LOCK_KEY = "itaas.suite.logoutProcessing";
const SAFETY_REDIRECT_MS = 10_000;
const IAM_LOGOUT_TIMEOUT_MS = 4_000;
const MODULE_SYNC_TIMEOUT_MS = 3_000;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function redirectToSuiteLogin(): void {
  const loginUrl = buildSuiteLoginUrl({ suiteUrl: window.location.origin });
  const separator = loginUrl.includes("?") ? "&" : "?";
  window.location.replace(`${loginUrl}${separator}stay=1`);
}

/** Déconnexion centralisée — purge IAM Suite + sessions locales de chaque module. */
export function SuiteLogoutPage() {
  useEffect(() => {
    let redirected = false;
    const goLogin = () => {
      if (redirected) return;
      redirected = true;
      sessionStorage.removeItem(LOGOUT_LOCK_KEY);
      clearLogoutPending();
      redirectToSuiteLogin();
    };

    const safetyTimer = window.setTimeout(goLogin, SAFETY_REDIRECT_MS);

    const run = async () => {
      const lockTs = sessionStorage.getItem(LOGOUT_LOCK_KEY);
      if (lockTs && Date.now() - Number(lockTs) < SAFETY_REDIRECT_MS) {
        await delay(500);
        goLogin();
        return;
      }
      sessionStorage.setItem(LOGOUT_LOCK_KEY, String(Date.now()));

      try {
        await Promise.race([
          suiteSessionApi.logout(),
          delay(IAM_LOGOUT_TIMEOUT_MS).then(() => {
            purgeAuthState();
          }),
        ]);
      } catch {
        purgeAuthState();
      }

      clearReturnTo();

      try {
        await Promise.race([
          syncModuleLogoutIframes(listModuleBaseUrls()),
          delay(MODULE_SYNC_TIMEOUT_MS),
        ]);
      } catch {
        // Sync best-effort — la redirection login reste prioritaire.
      } finally {
        window.clearTimeout(safetyTimer);
        goLogin();
      }
    };

    void run();

    return () => {
      window.clearTimeout(safetyTimer);
    };
  }, []);

  return (
    <div className="suite-center-message">
      <p className="text-muted mb-3">Déconnexion…</p>
      <button
        type="button"
        className="btn btn-link btn-sm text-muted"
        onClick={() => redirectToSuiteLogin()}
      >
        Continuer vers la connexion
      </button>
    </div>
  );
}
